import { useState, useCallback, useEffect } from 'react';
import { IResponseSrikaNode, ISrikaItem } from '../../../../../../modules/API/Gathering/models/srika';
import BarcodeScannerService from '../services/barcodeScannerService';

type UseBarcodeScanner = {
  input: string;
  setInput: (value: string) => void;
  barcodeList: IResponseSrikaNode | null;
  isLoading: boolean;
  error: string | null;
  scanBarcode: (onSuccess: (data: ISrikaItem, fullResponse: IResponseSrikaNode) => void, overrideBarcode?: string) => Promise<void>;
  loadBarcodeList: () => Promise<void>;
};

/**
 * Custom hook for barcode scanning functionality
 * 
 * @param autoLoadList - Automatically load barcode list on mount (default: true)
 * @returns Object with barcode scanner state and methods
 * 
 * @example
 * const { input, setInput, scanBarcode, barcodeList } = useBarcodeScanner();
 * 
 * const handleSuccess = (data, fullResponse) => {
 *   console.log('Scanned:', data);
 * };
 * 
 * <BarcodeScannerPanel
 *   input={input}
 *   setInput={setInput}
 *   onScan={() => scanBarcode(handleSuccess)}
 *   barcodeList={barcodeList?.entryLine}
 * />
 */
const useBarcodeScanner = (autoLoadList: boolean = true): UseBarcodeScanner => {
  const [input, setInput] = useState<string>('');
  const [barcodeList, setBarcodeList] = useState<IResponseSrikaNode | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  /**
   * Load list of available barcodes
   */
  const loadBarcodeList = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      
      const res = await BarcodeScannerService.getBarcodeList();
      setBarcodeList(res);
    } catch (err) {
      const errorMessage = 'Failed to load barcode list';
      setError(errorMessage);
      console.error(errorMessage, err);
    } finally {
      setIsLoading(false);
    }
  }, []);

  /**
   * Scan barcode and execute callback on success
   */
  const scanBarcode = useCallback(
    async (onSuccess: (data: ISrikaItem, fullResponse: IResponseSrikaNode) => void, overrideBarcode?: string) => {
      // overrideBarcode covers hardware scans captured while the input is not
      // focused — the value arrives directly, not via the (async) input state.
      const code = overrideBarcode ?? input;
      if (!BarcodeScannerService.isValidBarcodeFormat(code)) {
        setError('Invalid barcode format');
        return;
      }

      try {
        setIsLoading(true);
        setError(null);

        const res = await BarcodeScannerService.getEntryByBarcode(code);
        const data = res.entryLine[0];

        if (!data) {
          throw new Error('No data found for this barcode');
        }

        onSuccess(data, res);
      } catch (err) {
        const errorMessage = 'לא נמצא פריט עם קוד זה';
        setError(errorMessage);
        console.error('Barcode scan error:', err);
        throw err;
      } finally {
        setIsLoading(false);
      }
    },
    [input]
  );

  // Auto-load barcode list on mount if enabled
  useEffect(() => {
    if (autoLoadList) {
      loadBarcodeList();
    }
  }, [autoLoadList, loadBarcodeList]);

  return {
    input,
    setInput,
    barcodeList,
    isLoading,
    error,
    scanBarcode,
    loadBarcodeList,
  };
};

export default useBarcodeScanner;

